// Cola local de adjuntos comprimidos (§9). Cada registro guarda el Blob y sus metadatos juntos;
// las pestañas se avisan cambios por BroadcastChannel.
import { getDb, type QueuePhase, type QueueRecord } from '../local/db.ts';

export type { QueueRecord, QueuePhase };

type Listener = () => void;
const listeners = new Set<Listener>();
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('servicioos-queue') : null;
channel?.addEventListener('message', () => listeners.forEach((l) => l()));

export function notifyQueueChanged(): void {
  listeners.forEach((l) => l());
  channel?.postMessage('changed');
}

export function subscribeQueue(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function enqueue(record: QueueRecord): Promise<void> {
  await (await getDb()).put('queue', record);
  notifyQueueChanged();
}

export async function listQueue(userId: string, orderId?: string): Promise<QueueRecord[]> {
  const db = await getDb();
  const items = orderId
    ? await db.getAllFromIndex('queue', 'byUserOrder', [userId, orderId])
    : await db.getAllFromIndex('queue', 'byUser', userId);
  return items.sort((a, b) => a.createdAt - b.createdAt);
}

export async function updateQueue(id: string, patch: Partial<Omit<QueueRecord, 'id'>>): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('queue', 'readwrite');
  const current = await tx.store.get(id);
  if (current) await tx.store.put({ ...current, ...patch, updatedAt: Date.now() });
  await tx.done;
  notifyQueueChanged();
}

export async function removeQueue(id: string): Promise<void> {
  await (await getDb()).delete('queue', id);
  notifyQueueChanged();
}

/** Nueva identidad de carga para una reserva expirada o eliminada, conservando el Blob. */
export async function replaceQueueId(oldId: string, newId: string): Promise<QueueRecord | undefined> {
  const db = await getDb();
  const tx = db.transaction('queue', 'readwrite');
  const current = await tx.store.get(oldId);
  let next: QueueRecord | undefined;
  if (current) {
    next = { ...current, id: newId, phase: 'ready', error: null, updatedAt: Date.now() };
    await tx.store.delete(oldId);
    await tx.store.put(next);
  }
  await tx.done;
  notifyQueueChanged();
  return next;
}

/** Pendientes que bloquean Chequeo/Listo: todo lo que no está confirmado en servidor. */
export function isPending(item: QueueRecord): boolean {
  return item.phase !== 'confirmed';
}
